import React, { useState } from 'react';
import { Mail, MessageSquare, Shield, AlertCircle, CheckCircle, Ban, Trash2, Phone, Save, Loader2 } from 'lucide-react';

const SettingsPage = ({
  settings,
  setSettings,
  suppressionList,
  setSuppressionList,
  setContacts,
  onSave,
  settingsSaving,
  onSetAutomation,
  automationSaving
}) => {
  const [confirmOn, setConfirmOn] = useState(false);
  const [markedReady, setMarkedReady] = useState(false);
  const mode = settings?.tradeitDayAutomation || 'off';
  if (!settings) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg">
        <div className="text-sm text-gray-600">Loading settings...</div>
      </div>
    );
  }

  const credentials = settings.credentials || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-1">Configure API integrations, authentication, and compliance settings</p>
        </div>
        <button
          onClick={onSave}
          disabled={settingsSaving}
          className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-3 text-sm font-semibold text-white shadow-lg transition hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {settingsSaving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
          {settingsSaving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">TRADEIT DAY email</h2>
            <p className="mt-1 text-sm text-gray-600">
              Sending is off until you turn it on. Registration and unsubscribe still work.
            </p>
          </div>
          <div className={`rounded-full px-3 py-1 text-xs font-bold uppercase ${
            mode === 'on' ? 'bg-red-100 text-red-800' : mode === 'test' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'
          }`}>
            {mode}
          </div>
        </div>
        <div className="mt-5 grid gap-3 md:grid-cols-3">
          <button
            type="button"
            disabled={automationSaving || mode === 'off'}
            onClick={() => onSetAutomation('off')}
            className="rounded-xl border border-gray-200 px-4 py-3 text-left hover:border-gray-300 disabled:opacity-50"
          >
            <div className="text-sm font-semibold text-gray-900">Off</div>
            <div className="mt-1 text-xs text-gray-500">No TRADEIT DAY email goes out.</div>
          </button>
          <button
            type="button"
            disabled={automationSaving || mode === 'test'}
            onClick={() => onSetAutomation('test')}
            className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-left hover:border-amber-300 disabled:opacity-50"
          >
            <div className="text-sm font-semibold text-gray-900">Test</div>
            <div className="mt-1 text-xs text-gray-600">Mail only johncarterson101@gmail.com. Other signups are marked registered and wait.</div>
          </button>
          <button
            type="button"
            disabled={automationSaving || mode === 'on'}
            onClick={() => { setMarkedReady(false); setConfirmOn(true); }}
            className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-left hover:border-red-300 disabled:opacity-50"
          >
            <div className="text-sm font-semibold text-gray-900">Turn on automation</div>
            <div className="mt-1 text-xs text-gray-600">Starts with “A note from Kelly about October 15” for people who have not registered.</div>
          </button>
        </div>
        {confirmOn && (
          <div className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-gray-800">
              This emails the real list. People who already registered must be marked first, or they will get another registration email.
            </p>
            <label className="mt-3 flex items-start gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                checked={markedReady}
                onChange={(event) => setMarkedReady(event.target.checked)}
                className="mt-1"
              />
              Everyone who has already registered is marked registered.
            </label>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                disabled={!markedReady || automationSaving}
                onClick={() => onSetAutomation('on', true).then(() => setConfirmOn(false))}
                className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {automationSaving ? 'Turning on...' : 'Turn on automation'}
              </button>
              <button
                type="button"
                onClick={() => setConfirmOn(false)}
                className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3">
          <div className="text-xs font-semibold uppercase text-indigo-700">Email</div>
          <div className="mt-1 text-sm font-semibold text-gray-900">
            {credentials.sendgridConfigured ? 'Configured' : 'Missing SendGrid API key'}
          </div>
        </div>
        <div className="rounded-xl border border-purple-100 bg-purple-50 px-4 py-3">
          <div className="text-xs font-semibold uppercase text-purple-700">Twilio</div>
          <div className="mt-1 text-sm font-semibold text-gray-900">
            {credentials.twilioConfigured ? 'Credentials configured' : 'Missing SID or auth token'}
          </div>
        </div>
        <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3">
          <div className="text-xs font-semibold uppercase text-emerald-700">SMS Sender</div>
          <div className="mt-1 text-sm font-semibold text-gray-900">
            {credentials.smsReady ? 'Ready to send SMS' : 'Add a sender number or messaging service'}
          </div>
        </div>
      </div>

      {/* API Configuration */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* SendGrid Configuration */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl shadow-lg">
              <Mail className="text-white" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">SendGrid Configuration</h2>
              <p className="text-sm text-gray-600">Email delivery service</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">API Key</label>
              <input
                type="password"
                value={settings.sendgrid_api_key || ''}
                onChange={(e) => setSettings({ ...settings, sendgrid_api_key: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                placeholder={process.env.REACT_APP_SENDGRID_API_KEY || "SG.xxxxxxxxxxxxxxxxxxxxx"}
              />
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Sending Domain</label>
              <input
                type="text"
                value={settings.sending_domain || ''}
                onChange={(e) => setSettings({ ...settings, sending_domain: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                placeholder={process.env.REACT_APP_SENDING_DOMAIN || "mail.yourdomain.com"}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">From Name</label>
              <input
                type="text"
                value={settings.from_name || ''}
                onChange={(e) => setSettings({ ...settings, from_name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                placeholder={process.env.REACT_APP_FROM_NAME || "Your Company Team"}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">From Email</label>
              <input
                type="email"
                value={settings.from_email || ''}
                onChange={(e) => setSettings({ ...settings, from_email: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                placeholder={process.env.REACT_APP_FROM_EMAIL || "hello@yourdomain.com"}
              />
              <p className="text-xs text-gray-500 mt-1">
                Default sender for new templates. Each email template can override this with its own from name and from email.
              </p>
              {(settings.from_email || '').includes('noreply') && (
                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <AlertCircle size={12} />
                  Avoid "noreply" - reduces deliverability
                </p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="spf"
                  checked={settings.spf_configured}
                  onChange={(e) => setSettings({ ...settings, spf_configured: e.target.checked })}
                  className="rounded"
                />
                <label htmlFor="spf" className="text-sm font-semibold text-gray-700">SPF Record Configured</label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="dkim"
                  checked={settings.dkim_configured}
                  onChange={(e) => setSettings({ ...settings, dkim_configured: e.target.checked })}
                  className="rounded"
                />
                <label htmlFor="dkim" className="text-sm font-semibold text-gray-700">DKIM Signature Configured</label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="dmarc"
                  checked={settings.dmarc_configured}
                  onChange={(e) => setSettings({ ...settings, dmarc_configured: e.target.checked })}
                  className="rounded"
                />
                <label htmlFor="dmarc" className="text-sm font-semibold text-gray-700">DMARC Policy Configured</label>
              </div>
            </div>

            <div className="p-4 bg-indigo-50 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="text-indigo-600 flex-shrink-0 mt-0.5" size={18} />
                <div className="text-sm text-indigo-900">
                  <div className="font-semibold mb-1">Domain Authentication</div>
                  <div className="text-indigo-700">Configure SPF, DKIM & DMARC in SendGrid dashboard for 99% deliverability.</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Twilio Configuration */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl shadow-lg">
              <MessageSquare className="text-white" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Twilio Configuration</h2>
              <p className="text-sm text-gray-600">SMS delivery service</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Account SID</label>
              <input
                type="text"
                value={settings.twilio_account_sid || ''}
                onChange={(e) => setSettings({ ...settings, twilio_account_sid: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                placeholder={process.env.REACT_APP_TWILIO_ACCOUNT_SID || "ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"}
              />
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Auth Token</label>
              <input
                type="password"
                value={settings.twilio_auth_token || ''}
                onChange={(e) => setSettings({ ...settings, twilio_auth_token: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                placeholder={process.env.REACT_APP_TWILIO_AUTH_TOKEN || "********************************"}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Messaging Service SID</label>
              <input
                type="text"
                value={settings.twilio_messaging_service_sid || ''}
                onChange={(e) => setSettings({ ...settings, twilio_messaging_service_sid: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                placeholder="MG631e9e7ed9148d95a069cf5146be843a"
              />
              <p className="text-xs text-gray-500 mt-1">
                Defaults to your built-in Messaging Service <code className="text-gray-700">MG631e9e7ed9148d95a069cf5146be843a</code> for higher-volume sending.
                When you save settings, we set this service&apos;s{' '}
                <span className="font-medium text-gray-700">Inbound Request URL</span> and{' '}
                <span className="font-medium text-gray-700">Status Callback</span> to your API webhook (from{' '}
                <code className="text-gray-700">PUBLIC_API_URL</code> or <code className="text-gray-700">RENDER_EXTERNAL_URL</code>
                ) so replies and delivery events reach MarketReach. Disable with env{' '}
                <code className="text-gray-700">TWILIO_SKIP_MESSAGING_WEBHOOK_SYNC=1</code> if you configure webhooks only in Twilio.
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Twilio From Number</label>
              <input
                type="tel"
                value={settings.twilio_from_number || ''}
                onChange={(e) => setSettings({ ...settings, twilio_from_number: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                placeholder="+18339027623"
              />
              <p className="text-xs text-gray-500 mt-1">Fallback direct sender. The app defaults to `+18339027623` and prefers the built-in Messaging Service first.</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Company Name</label>
              <input
                type="text"
                value={settings.company_name || ''}
                onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                placeholder={process.env.REACT_APP_COMPANY_NAME || "Your Company"}
              />
            </div>

            <div className="p-4 bg-purple-50 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="text-purple-600 flex-shrink-0 mt-0.5" size={18} />
                <div className="text-sm text-purple-900">
                  <div className="font-semibold mb-1">TCPA Compliance Required</div>
                  <div className="text-purple-700">Obtain explicit consent before sending marketing SMS. Keep consent records with timestamps and make opt-out instructions visible in campaign copy.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Compliance Section */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl shadow-lg">
            <Shield className="text-white" size={24} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Compliance & Privacy</h2>
            <p className="text-sm text-gray-600">Legal requirements and best practices</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { 
              title: 'CAN-SPAM Act', 
              desc: 'All emails must include unsubscribe links, physical address, and honest subject lines',
              icon: CheckCircle, 
              color: 'green',
              implemented: true
            },
            { 
              title: 'GDPR Compliant', 
              desc: 'Data processing agreements, lawful basis for processing, right to deletion',
              icon: Shield, 
              color: 'blue',
              implemented: true
            },
            { 
              title: 'TCPA Compliant', 
              desc: 'Explicit consent required for SMS marketing, opt-out handling, consent records',
              icon: Phone, 
              color: 'purple',
              implemented: true
            }
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <div key={i} className={`p-5 rounded-xl border-2 ${item.implemented ? `border-${item.color}-200 bg-${item.color}-50` : 'border-gray-200 bg-gray-50'}`}>
                <div className="flex items-center justify-between mb-3">
                  <Icon className={`text-${item.color}-600`} size={24} />
                  {item.implemented && <CheckCircle className={`text-${item.color}-600`} size={20} />}
                </div>
                <div className="font-bold text-gray-900 mb-2">{item.title}</div>
                <div className="text-xs text-gray-700">{item.desc}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Suppression List Management */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Suppression List Management</h2>
          <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-bold">
            {suppressionList.length} suppressed
          </span>
        </div>
        
        {suppressionList.length > 0 ? (
          <div className="space-y-2">
            {suppressionList.slice(0, 10).map(item => (
              <div key={item.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Ban className="text-red-600" size={16} />
                  <div>
                    <div className="font-medium text-gray-900">{item.email || item.phone}</div>
                    <div className="text-xs text-gray-600">Reason: {item.reason} • {new Date(item.addedAt).toLocaleDateString()}</div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSuppressionList(prev => prev.filter(s => s.id !== item.id));
                    setContacts(prev => prev.map(c => 
                      (c.email === item.email || c.phone === item.phone) ? { ...c, status: 'active' } : c
                    ));
                  }}
                  className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors duration-200"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            {suppressionList.length > 10 && (
              <div className="text-center text-sm text-gray-500 py-2">
                + {suppressionList.length - 10} more
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Ban className="mx-auto mb-2 text-gray-400" size={32} />
            <p className="text-sm">No suppressed contacts</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;